import type {
  ButtonVariantProperties,
  IExtendedStyleProperties,
} from '@themes/types/extended';

import { alpha, type Theme } from '@mui/material/styles';

import {
  BUTTON_OUTLINED_HOVER_SHADOW_ALPHA,
  BUTTON_RIPPLE_OVERLAY_ALPHA,
} from '@themes/constants';
import { getColors } from '@themes/utils/getColors';
import { getShadow } from '@themes/utils/getShadow';

interface IButtonStyleProperties extends IExtendedStyleProperties {
  variant: ButtonVariantProperties;
}

function getColorStyle({ theme, color, variant }: IButtonStyleProperties) {
  const colors = getColors(theme, color);
  const { dark, main, lighter, contrastText } = colors;

  const buttonShadow = `${color ?? 'inherit'}Button`;
  const shadows = getShadow(theme, buttonShadow);

  const commonShadow = {
    '&:focus-visible': {
      outlineOffset: 2,
      outline: `2px solid ${dark}`,
    },
    '&::after': {
      boxShadow: `0 0 5px 5px ${alpha(main, BUTTON_RIPPLE_OVERLAY_ALPHA)}`,
    },
    '&:active::after': {
      boxShadow: `0 0 0 0 ${alpha(main, BUTTON_RIPPLE_OVERLAY_ALPHA)}`,
    },
  };

  switch (variant) {
    case 'contained': {
      return {
        '&:hover': {
          backgroundColor: dark,
        },
        ...commonShadow,
      };
    }
    case 'shadow': {
      return {
        boxShadow: shadows,
        color: contrastText,
        backgroundColor: main,
        '&:hover': {
          boxShadow: 'none',
          backgroundColor: dark,
        },
        ...commonShadow,
      };
    }
    case 'outlined': {
      return {
        color: dark,
        borderColor: dark,
        '&:hover': {
          borderColor: dark,
          backgroundColor: 'transparent',
          boxShadow: `0 0 1px 1px ${alpha(main, BUTTON_OUTLINED_HOVER_SHADOW_ALPHA)}`,
        },
        ...commonShadow,
      };
    }
    case 'dashed': {
      return {
        color: main,
        borderColor: main,
        backgroundColor: lighter,
        '&:hover': {
          color: dark,
          borderColor: dark,
        },
        ...commonShadow,
      };
    }
    default: {
      return {
        '&:hover': {
          color: dark,
          backgroundColor: lighter,
        },
        ...commonShadow,
      };
    }
  }
}

export default function Button(theme: Theme) {
  const primaryDashed = getColorStyle({
    theme,
    color: 'primary',
    variant: 'dashed',
  });
  const primaryShadow = getColorStyle({
    theme,
    color: 'primary',
    variant: 'shadow',
  });

  const disabledStyle = {
    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[200],
    },
  };
  const iconStyle = {
    '&>*:nth-of-type(1)': {
      fontSize: 'inherit',
    },
  };

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        endIcon: {
          ...iconStyle,
        },
        startIcon: {
          ...iconStyle,
        },
        outlined: {
          ...disabledStyle,
        },
        contained: {
          ...disabledStyle,
        },
        sizeExtraSmall: {
          minWidth: 56,
          padding: '2px 8px',
          fontSize: '0.625rem',
        },
        text: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        shadow: {
          ...primaryShadow,
          '&.MuiButton-shadowInfo': getColorStyle({
            theme,
            color: 'info',
            variant: 'shadow',
          }),
          '&.MuiButton-shadowError': getColorStyle({
            theme,
            color: 'error',
            variant: 'shadow',
          }),
          '&.MuiButton-shadowWarning': getColorStyle({
            theme,
            color: 'warning',
            variant: 'shadow',
          }),
          '&.MuiButton-shadowSuccess': getColorStyle({
            theme,
            color: 'success',
            variant: 'shadow',
          }),
          '&.MuiButton-shadowPrimary': getColorStyle({
            theme,
            color: 'primary',
            variant: 'shadow',
          }),
          '&.MuiButton-shadowSecondary': getColorStyle({
            theme,
            variant: 'shadow',
            color: 'secondary',
          }),
          '&.Mui-disabled': {
            color: `${theme.palette.grey[300]} !important`,
            borderColor: `${theme.palette.grey[400]} !important`,
            backgroundColor: `${theme.palette.grey[200]} !important`,
          },
        },
        dashed: {
          border: '1px dashed',
          ...primaryDashed,
          '&.MuiButton-dashedInfo': getColorStyle({
            theme,
            color: 'info',
            variant: 'dashed',
          }),
          '&.MuiButton-dashedError': getColorStyle({
            theme,
            color: 'error',
            variant: 'dashed',
          }),
          '&.MuiButton-dashedWarning': getColorStyle({
            theme,
            color: 'warning',
            variant: 'dashed',
          }),
          '&.MuiButton-dashedSuccess': getColorStyle({
            theme,
            color: 'success',
            variant: 'dashed',
          }),
          '&.MuiButton-dashedPrimary': getColorStyle({
            theme,
            color: 'primary',
            variant: 'dashed',
          }),
          '&.MuiButton-dashedSecondary': getColorStyle({
            theme,
            variant: 'dashed',
            color: 'secondary',
          }),
          '&.Mui-disabled': {
            color: `${theme.palette.grey[300]} !important`,
            borderColor: `${theme.palette.grey[400]} !important`,
            backgroundColor: `${theme.palette.grey[200]} !important`,
          },
        },
        root: {
          fontWeight: 400,
          '&:active::after': {
            top: 0,
            left: 0,
            opacity: 1,
            borderRadius: 4,
            transition: '0s',
            position: 'absolute',
          },

          '&::after': {
            top: 0,
            left: 0,
            opacity: 0,
            width: '100%',
            content: '""',
            height: '100%',
            borderRadius: 4,
            display: 'block',
            position: 'absolute',
            transition: 'all 0.5s',
          },
          variants: [
            {
              props: { color: 'info', variant: 'text' },
              style: getColorStyle({ theme, color: 'info', variant: 'text' }),
            },
            {
              props: { color: 'error', variant: 'text' },
              style: getColorStyle({ theme, color: 'error', variant: 'text' }),
            },
            {
              props: { variant: 'text', color: 'warning' },
              style: getColorStyle({
                theme,
                variant: 'text',
                color: 'warning',
              }),
            },
            {
              props: { variant: 'text', color: 'success' },
              style: getColorStyle({
                theme,
                variant: 'text',
                color: 'success',
              }),
            },
            {
              props: { variant: 'text', color: 'primary' },
              style: getColorStyle({
                theme,
                variant: 'text',
                color: 'primary',
              }),
            },
            {
              props: { variant: 'text', color: 'secondary' },
              style: getColorStyle({
                theme,
                variant: 'text',
                color: 'secondary',
              }),
            },
            {
              props: { color: 'info', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'info',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'error', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'error',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'warning', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'warning',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'success', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'success',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'primary', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'primary',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'secondary', variant: 'outlined' },
              style: getColorStyle({
                theme,
                color: 'secondary',
                variant: 'outlined',
              }),
            },
            {
              props: { color: 'info', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'info',
                variant: 'contained',
              }),
            },
            {
              props: { color: 'error', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'error',
                variant: 'contained',
              }),
            },
            {
              props: { color: 'warning', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'warning',
                variant: 'contained',
              }),
            },
            {
              props: { color: 'success', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'success',
                variant: 'contained',
              }),
            },
            {
              props: { color: 'primary', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'primary',
                variant: 'contained',
              }),
            },
            {
              props: { color: 'secondary', variant: 'contained' },
              style: getColorStyle({
                theme,
                color: 'secondary',
                variant: 'contained',
              }),
            },
          ],
        },
      },
    },
  };
}
